import React, { useEffect, useState, useRef, Fragment } from 'react';
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { ComparisonJudgeConfig } from '../../../content/types';
export function ComparisonJudgeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data?.comparisonJudge || question.data || {}) as ComparisonJudgeConfig;
  const items = config.items || [];
  const criterion = config.criterion || '';
  const correctIndex = config.correctIndex ?? -1;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setSelectedIndex(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null);
  }, [selectedIndex, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current && selectedIndex !== null) {
      prevValidateRef.current = true;
      onAnswer(selectedIndex);
    }
  }, [shouldValidate, selectedIndex, onAnswer]);
  const handleItemClick = (index: number) => {
    if (shouldValidate) return;
    setSelectedIndex(index);
  };
  if (items.length < 2) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-7276">
        <p className="text-red-700 font-bold" data-id="element-7277">
          ⚠️ Configuratie ontbreekt (minimaal 2 items vereist)
        </p>
      </div>;
  }
  return <div className="w-full max-w-4xl mx-auto flex flex-col gap-8" data-id="element-7278">
      <div className="flex flex-row gap-4 sm:gap-6 justify-center items-stretch relative" data-id="element-7279">
        {items.map((item, index) => {
        const isSelected = selectedIndex === index;
        let validationClass = 'bg-white border-slate-200 text-slate-700';
        let ValidationIcon = null;
        if (shouldValidate) {
          const isCorrect = index === correctIndex;
          if (isSelected) {
            validationClass = isCorrect ? 'bg-emerald-50 border-emerald-500 text-emerald-900 ring-4 ring-emerald-100' : 'bg-red-50 border-red-500 text-red-900 ring-4 ring-red-100';
            ValidationIcon = isCorrect ? <CheckCircle2 className="w-8 h-8 text-emerald-500 absolute top-4 right-4" data-id="element-7280" /> : <XCircle className="w-8 h-8 text-red-500 absolute top-4 right-4" data-id="element-7281" />;
          } else if (isCorrect) {
            validationClass = 'bg-emerald-50/50 border-emerald-300 text-emerald-800';
            ValidationIcon = <CheckCircle2 className="w-8 h-8 text-emerald-400 absolute top-4 right-4" data-id="element-7282" />;
          } else {
            validationClass = 'bg-slate-50 border-slate-200 text-slate-400 opacity-50';
          }
        } else if (isSelected) {
          validationClass = 'bg-indigo-50 border-indigo-500 text-indigo-900 ring-4 ring-indigo-100 scale-105';
        }
        return <Fragment key={item.id || index} data-id="element-7283">
              <motion.button whileTap={shouldValidate ? {} : {
            scale: 0.98
          }} onClick={() => handleItemClick(index)} disabled={shouldValidate} className={`relative flex-1 p-4 sm:p-6 rounded-3xl border-4 transition-all shadow-sm flex flex-col items-center justify-center gap-2 min-h-[120px] ${shouldValidate ? 'cursor-default' : 'cursor-pointer hover:-translate-y-2 hover:shadow-lg hover:border-indigo-300'} ${validationClass}`} aria-pressed={isSelected} data-id="element-7284">
                {ValidationIcon}

                <div className="text-2xl sm:text-3xl font-black text-center break-words w-full" data-id="element-7285">
                  {item.content}
                </div>

                {item.label && <div className="text-sm sm:text-base font-bold opacity-60 uppercase tracking-wider" data-id="element-7286">
                    {item.label}
                  </div>}
              </motion.button>
              {index === 0 && <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-slate-100 border-4 border-white shadow-sm text-slate-400 font-black text-sm sm:text-base" data-id="element-7287">
                  VS
                </div>}
            </Fragment>;
      })}
      </div>
    </div>;
}